import type { PermissionGroupId } from '@strength/domain';
import { useTranslation } from 'react-i18next';
import { useViewer } from '../../context/viewer';

/**
 * Single source of truth for the library tabs — both the nav links
 * (`useLibraryTabs`) and the route guards (`tabGuard`, SC-4792) read from
 * this list, so they cannot disagree. Each tab is governed by its
 * library group's View permission. The four tag-like tabs (Tags, Exercise
 * Types, Equipment, Body Parts) all ride on the Tags group. Legacy roles
 * resolve to always-granted, so they see every tab.
 */
export const LIBRARY_TABS = [
  {
    to: '/$teamId/library/exercises' as const,
    labelKey: 'exercises',
    permissionAccess: 'library.exercises.view',
  },
  {
    to: '/$teamId/library/schemes' as const,
    labelKey: 'schemes',
    permissionAccess: 'library.schemes.view',
  },
  {
    to: '/$teamId/library/max-conversions' as const,
    labelKey: 'maxConversions',
    permissionAccess: 'library.exerciseMaxConversions.view',
  },
  {
    to: '/$teamId/library/workout-templates' as const,
    labelKey: 'templates',
    permissionAccess: 'library.templates.view',
  },
  {
    to: '/$teamId/library/tags' as const,
    labelKey: 'tags',
    permissionAccess: 'library.tags.view',
  },
  {
    to: '/$teamId/library/exercise-types' as const,
    labelKey: 'exerciseType',
    permissionAccess: 'library.tags.view',
  },
  {
    to: '/$teamId/library/equipment' as const,
    labelKey: 'equipment',
    permissionAccess: 'library.tags.view',
  },
  {
    to: '/$teamId/library/body-parts' as const,
    labelKey: 'bodyParts',
    permissionAccess: 'library.tags.view',
  },
] as const;

/**
 * The tab's URL path segment (e.g. 'exercises', 'schemes'), used for
 * matching the active tab against matched routes.
 */
export function getLibraryTabSegment(tab: { to: string }) {
  return tab.to.split('/').at(-1) ?? '';
}

/**
 * The library tabs the current viewer has access to, resolved through the
 * viewer's permissions. Returns links ready for TanStack Router `<Link />`
 * (with translated labels) in display order.
 */
export function useLibraryTabs() {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();

  return LIBRARY_TABS.filter(({ permissionAccess }) => {
    // permissionAccess is the full dotted key — e.g. 'library.exercises.view'
    const group = permissionAccess.slice(
      0,
      permissionAccess.lastIndexOf('.'),
    ) as PermissionGroupId;
    const action = permissionAccess.slice(
      permissionAccess.lastIndexOf('.') + 1,
    ) as 'view';
    return hasPermissions(group, action);
  }).map(({ labelKey, ...tab }) => ({
    ...tab,
    label: t(labelKey),
  }));
}
